import React, { useState } from 'react';
import {
    Users, BarChart2, Plus, Settings,
    ShieldAlert, Sparkles, Share2
} from 'lucide-react';
import { useTeam } from '../../hooks/useTeam';
import { useSubscription } from '../../hooks/useSubscription';
import MemberCard from '../../components/team/MemberCard';
import AbilityRadarChart from '../../components/team/AbilityRadarChart';
import { InviteModal } from '../../components/team/InviteModal';
import { Link } from 'react-router-dom';

const TeamDashboard = () => {
    const { team, loading, createTeam, members } = useTeam();
    const { isBusiness } = useSubscription();
    const [activeTab, setActiveTab] = useState('overview');
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    // Mock data for radar chart until aggregation is ready
    const mockTeamData = {
        integrationThinking: 75,
        adaptiveDevelopment: 60,
        reflection: 85,
        balance: 50,
        innovation: 65,
        empathy: 90,
        collaboration: 80,
        initiative: 70
    };

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading team data...</div>;
    }

    if (!team) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-3xl p-12 text-center text-white shadow-2xl overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                        </svg>
                    </div>

                    <div className="relative z-10 max-w-2xl mx-auto">
                        <div className="bg-white/10 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm shadow-inner">
                            <Users className="w-10 h-10 text-indigo-300" />
                        </div>

                        <h1 className="text-3xl md:text-5xl font-bold mb-4 font-display">
                            チームで、もっと速く遠くへ。
                        </h1>
                        <p className="text-lg text-indigo-200 mb-8 leading-relaxed">
                            メンバーの強みを可視化し、組織のポテンシャルを最大限に引き出します。<br />
                            互いの個性を理解し、補完しあう最強のチームを作りましょう。
                        </p>

                        {!isBusiness ? (
                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                                <p className="text-sm font-bold text-indigo-200 uppercase tracking-wider mb-2">Business Plan Required</p>
                                <p className="mb-4">チーム機能を利用するには、Businessプランへのアップグレードが必要です。</p>
                                <Link
                                    to="/subscription?plan=business"
                                    className="inline-block py-3 px-8 bg-white text-indigo-900 font-bold rounded-full hover:bg-indigo-50 transition-all shadow-lg transform hover:-translate-y-1"
                                >
                                    Businessプランを見る
                                </Link>
                            </div>
                        ) : (
                            <button
                                onClick={() => createTeam('My New Team')} // Simple creation for now
                                className="py-4 px-10 bg-indigo-500 hover:bg-indigo-400 text-white font-bold rounded-full shadow-lg shadow-indigo-900/50 transition-all transform hover:-translate-y-1 flex items-center gap-2 mx-auto"
                            >
                                <Plus className="w-6 h-6" />
                                チームを作成する
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        {team.name}
                        <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full border border-indigo-200">Business</span>
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">チームメンバーの成長とバランスを管理</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setIsInviteModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        メンバー招待
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                        <Settings className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'overview'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <BarChart2 className="w-4 h-4" />
                        組織分析
                    </button>
                    <button
                        onClick={() => setActiveTab('members')}
                        className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'members'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        <Users className="w-4 h-4" />
                        メンバー管理
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{members?.length || 0}</span>
                    </button>
                </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Radar Chart Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-amber-500" />
                                チームアビリティ
                            </h3>
                            <button className="text-xs text-indigo-600 font-medium hover:underline">
                                詳細レポート
                            </button>
                        </div>
                        <div className="bg-indigo-50/50 rounded-xl p-4">
                            <AbilityRadarChart data={mockTeamData} />
                        </div>
                        <div className="mt-6">
                            <h4 className="font-bold text-sm text-gray-700 mb-3">チームの強み</h4>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">共感力 (High)</span>
                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">振り返り力 (High)</span>
                            </div>
                        </div>
                    </div>

                    {/* Stats / Insights */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white text-center">
                            <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Team Engagement</p>
                            <div className="flex justify-center items-end gap-2">
                                <span className="text-5xl font-bold">85</span>
                                <span className="text-xl text-gray-400 mb-2">%</span>
                            </div>
                            <p className="text-sm text-gray-400 mt-2">昨月比 +12%</p>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h3 className="font-bold text-gray-800 mb-4">最近のアクティビティ</h3>
                            <div className="space-y-4">
                                {[1, 2, 3].map((_, i) => (
                                    <div key={i} className="flex gap-3 text-sm">
                                        <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0" />
                                        <div>
                                            <p className="text-gray-700"><span className="font-bold">田中 太郎</span>さんが「適応発達力」の診断を完了しました。</p>
                                            <p className="text-gray-400 text-xs">2時間前</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {members?.map(member => (
                            <MemberCard
                                key={member.id}
                                member={member}
                                onAction={() => { }}
                            />
                        ))}
                        {(!members || members.length === 0) && (
                            <div className="col-span-full py-12 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                                メンバーがまだいません。招待しましょう！
                            </div>
                        )}
                    </div>
                </div>
            )}

            <InviteModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
            />
        </div>
    );
};

export default TeamDashboard;
